import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import moment from 'moment';
import { MaterialModule } from '../../../../shared/patterns/material-module';

export type PaymentDateForm = FormGroup<{
  from: FormControl<string>;
  to: FormControl<string>;
}>;

@Component({
  selector: 'app-payment-date-form',
  templateUrl: './payment-date-form.component.html',
  styleUrls: ['./payment-date-form.component.scss'],
  imports: [MaterialModule],
})
export class PaymentDateFormComponent implements OnInit {
  @Input() parentForm: FormGroup<{ date?: PaymentDateForm }>;

  get dateForm() {
    return this.parentForm.controls.date;
  }

  get dateFromControl() {
    return this.dateForm.controls.from;
  }

  get dateToControl() {
    return this.dateForm.controls.to;
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    const { from, to } = this.calculatePresetDateRange(-1);

    this.parentForm.addControl(
      'date',
      this.fb.group(
        {
          from: [from, Validators.required],
          to: [to, Validators.required],
        },
        {
          validators: [this.dateValidator()],
        }
      )
    );
  }

  selectPresetDateRange(offsetMonths: number) {
    const { from, to } = this.calculatePresetDateRange(offsetMonths);

    this.parentForm.controls.date.patchValue({
      from,
      to,
    });
  }

  getDateInputError(control: FormControl) {
    if (!Object.keys(control.errors || {}).length) {
      return undefined;
    }

    if (control.getError('dateRangeInvalid')) {
      return 'Invalid date range';
    }

    if (control.getError('dateRangeTooBig')) {
      return 'Date range too large';
    }

    return control.getError('required') ? 'Required' : 'Invalid';
  }

  private dateValidator() {
    return (form: PaymentDateForm): ValidationErrors | null => {
      const dateFromControl = form.controls.from;
      const dateToControl = form.controls.to;

      if (
        dateFromControl.getError('required') ||
        dateToControl.getError('required')
      ) {
        return null;
      }

      const dateFrom = moment(dateFromControl.value);
      const dateTo = moment(dateToControl.value);

      if (dateTo.isBefore(dateFrom)) {
        dateFromControl.setErrors({ dateRangeInvalid: true });
        dateToControl.setErrors({ dateRangeInvalid: true });
        return null;
      }

      if (dateTo.diff(dateFrom, 'year') >= 1) {
        dateFromControl.setErrors({ dateRangeTooBig: true });
        dateToControl.setErrors({ dateRangeTooBig: true });
        return null;
      }

      dateFromControl.setErrors(null);
      dateToControl.setErrors(null);

      return null;
    };
  }

  private calculatePresetDateRange(offsetMonths = 0) {
    const from = moment().add(offsetMonths, 'month').startOf('month');

    return {
      from: from.format('YYYY-MM-DD'),
      to: from.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
}
